import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type AbsenceFormSchema, absenceFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AbsenceForm } from "@/forms/absence-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/** Props Interface */
interface AddAbsenceModalProps extends ModalProps {
	header?: React.ReactNode;
	onComplete?: () => Promise<void>;
	userId?: string;
	isDisabled?: boolean;
}

export function AddAbsenceModal({
	isOpen,
	onClose,
	userId,
	onComplete,
	header,
	isDisabled,
}: AddAbsenceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const auth = useAuth();
	const qc = useQueryClient();

	/** Form */
	const methods = useForm({
		resolver: resolver(absenceFormSchema),
		defaultValues: {
			startDate: moment().add(1, "week").startOf("week").db(),
			endDate: moment().add(1, "week").endOf("week").db(),
		},
	});

	/** Functions */
	async function handleOnSubmit(json: AbsenceFormSchema) {
		const response = await client.users[":userId"].absences.$post({
			param: {
				userId: guardString(userId || auth.user?.id),
			},
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["absences", "absence"]),
		});
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("absences.add_absence")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					{header && header}
					<AbsenceForm />
				</Modal.Body>
				<Modal.FooterForm isDisabled={isDisabled} />
			</Modal>
		</FormProvider>
	);
}
